import { isAdminSpaceUser } from '@/utils/accessControlUtils';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BalanceValidationApi from '../../../../../../api/BalanceValidationApi';
import ModelEconomiquePartenaireApi from '../../../../../../api/ModelEconomiquePartenaireApi';
import TicketPartenaireApi from '../../../../../../api/TicketPartenaireApi';
import PaymentMethodFields from './PaymentMethodFields';
import PartnerCreditInsufficiencyNotice from './PartnerCreditInsufficiencyNotice';
import TamarraDebtorDepositUi from './TamarraDebtorDepositUi';
import { buildMoteurInsufficientCreditTicketPayload } from './buildMoteurInsufficientCreditTicketPayload';
import { message } from '../utils/notification';
import { usePartnerPriceDisplay } from '../context/usePartnerPriceDisplay';
import {
  convertHotelAmountForPartner,
  resolveHotelDisplayCurrency,
} from '../utils/partnerRateMap';

import moment from 'moment'
const normalizeMethodLabelForMatch = raw =>
  String(raw || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/** Admin manual fallback: Espèces, Chèque, Carte bancaire (label match) */
const isAdminManualAllowedPaymentLabel = raw => {
  const l = normalizeMethodLabelForMatch(raw);
  if (l.includes('espece') || l.includes('cash')) return true;
  if (l.includes('check') || l.includes('cheque')) return true;
  if ((l.includes('carte') && l.includes('bancaire')) || l.includes('carte bancaire')) return true;
  return false;
};

const pickDefaultEspèceModeId = (ids, labelsById) => {
  const cashId = ids.find(id => {
    const lab = normalizeMethodLabelForMatch(labelsById[String(id)] || '');
    return lab.includes('espece') || lab.includes('cash');
  });
  return cashId || ids[0];
};

const AutomaticPaymentMethods = ({
  client,
  hotel = null,
  totalAmount,
  value,
  onChange,
  disabled = false,
  partnerDisplay = null,
  bookingDate = null,
  partnerIdOverride = null,
  insufficientCreditTicketContext = null,
  onPartnerInsufficientBalanceChange = null,
  /** When set (e.g. eco model min / amount to pay now), admin manual fallback uses this instead of room total */
  economicModelPaymentAmount = null,
  /** Partner / affilié: amount fields read-only; show deduction notice */
  lockPaymentAmount = false,
  lockPaymentAmountMessage = null,
  appearance = 'default',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAsTnd, displayInputToTnd } = usePartnerPriceDisplay(partnerDisplay);
  const displayCurrency = resolveHotelDisplayCurrency(hotel, partnerDisplay, {
    showAsTnd,
  });
  const displayAmount = (val) =>
    partnerDisplay
      ? convertHotelAmountForPartner(val, hotel, partnerDisplay, {
          toTnd: showAsTnd,
        })
      : Number(Number(val).toFixed(2));
  const displayToTnd = (val) => displayInputToTnd(val);
  const [orderedMethods, setOrderedMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({});
  const [totalCalculated, setTotalCalculated] = useState(0);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [availableMethods, setAvailableMethods] = useState([]);
  const [methodLabelsById, setMethodLabelsById] = useState({});
  const [partnerModesById, setPartnerModesById] = useState({});
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceDisplay, setBalanceDisplay] = useState(null);
  const [b2bInsufficientDepositAndPlafond, setB2bInsufficientDepositAndPlafond] = useState(false);
  const [ticketCreating, setTicketCreating] = useState(false);

  // Get current user for partner space
  const getCurrentUser = () => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  };
  const currentUser = getCurrentUser();
  const isAdminUser = isAdminSpaceUser(currentUser);

  // Determine which client ID to use for payment methods (solde / crédit partenaire)
  const getEffectiveClientId = () => {
    if (partnerIdOverride != null && partnerIdOverride !== '') {
      const coerced = Number(partnerIdOverride);
      if (!Number.isNaN(coerced) && coerced > 0) return coerced;
    }

    const currentUser = getCurrentUser();

    // Case 1: Partner space — balance is on the partner client id
    if (currentUser && currentUser.role === 'client') {
      return (
        Number(currentUser.user_partner_id) ||
        Number(currentUser.id_client) ||
        Number(currentUser.id) ||
        null
      );
    }

    // Case 2: Affiliate space — parent partner balance
    if (currentUser && currentUser.role === 'affilie') {
      return (
        Number(currentUser.id_partenaire_parent) ||
        Number(currentUser.partner_id) ||
        Number(currentUser.user_partner_id) ||
        Number(client?.id) ||
        null
      );
    }

    // Case 3: Admin space - use the selected client from dropdown
    return client?.id;
  };

  const isPartnerUserForTicket = () => {
    const u = getCurrentUser();
    return u?.role === 'client' || u?.userType === 'partner';
  };

  const handleOpenSupplierReclamationTicket = async () => {
    if (!isPartnerUserForTicket() || ticketCreating) return;
    setTicketCreating(true);
    try {
      const ctx = insufficientCreditTicketContext || {};
      const payload = buildMoteurInsufficientCreditTicketPayload({
        t,
        client,
        hotel: ctx.hotel ?? null,
        checkIn: bookingDate,
        checkOut: ctx.checkoutDate ?? null,
        roomsConfig: ctx.roomsConfig ?? [],
        economicModelLabel: ctx.economicModelLabel ?? null,
        billingLabel: ctx.billingLabel ?? null,
        totalAmountTnd: parseFloat(totalAmount) || 0,
        displayAmount,
        displayCurrency,
        balanceInfo,
      });
      const ticket = await TicketPartenaireApi.create(payload);
      const id = ticket?.id;
      message.success(
        t('moteurReservation.automaticPaymentMethods.ticket_created_success', {
          defaultValue: 'Ticket créé. Vous allez être redirigé.',
        })
      );
      if (id != null) navigate(`/support-tickets/ticket/${id}`);
      else navigate('/support-tickets/liste');
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          t('moteurReservation.automaticPaymentMethods.ticket_created_error', {
            defaultValue: 'Impossible de créer le ticket',
          })
      );
    } finally {
      setTicketCreating(false);
    }
  };

  // Normalize payment method names to backend format (for backward compatibility)
  const normalizePaymentMethodName = methodName => {
    const normalizationMap = {
      Débiteur: 'debiteur',
      Deposit: 'deposit',
      'Totalité en ligne': 'totalite_en_ligne',
      'Total-Marge': 'total_marge',
      debiteur: 'debiteur',
      deposit: 'deposit',
      totalite_en_ligne: 'totalite_en_ligne',
      total_marge: 'total_marge',
    };

    const normalized = normalizationMap[methodName] || methodName;
    return normalized;
  };

  // Normalize payment methods object for backend (for backward compatibility)
  const normalizePaymentMethods = methods => {
    const normalized = {};
    Object.entries(methods).forEach(([key, value]) => {
      const normalizedKey = normalizePaymentMethodName(key);
      normalized[normalizedKey] = value;
    });
    return normalized;
  };

  const extractAmount = (paymentValue) => {
    if (paymentValue == null) return 0;
    if (typeof paymentValue === 'object' && paymentValue.amount !== undefined) {
      return Number(paymentValue.amount || 0);
    }
    return Number(paymentValue || 0);
  };

  /** Same calendar day as payment-eligibility plafond check (avoids “no plafond” on today vs check-in date). */
  const toBalanceRefDate = (refDate) => {
    if (refDate == null || refDate === '') return null;
    if (typeof refDate === 'string') {
      const iso = refDate.match(/^(\d{4}-\d{2}-\d{2})/);
      if (iso) return iso[1];
    }
    if (typeof refDate?.format === 'function') {
      const s = refDate.format('YYYY-MM-DD');
      return s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
    }
    const d = moment.isMoment(refDate) ? refDate : moment(refDate);
    if (!Number.isNaN(d.valueOf())) return d.format('YYYY-MM-DD');
    return null;
  };

  const isB2BForEligibility = () => {
    const seg = String(client?.segment || '').toUpperCase();
    if (seg === 'B2C') return false;
    if (seg && seg !== 'B2C') return true;
    const u = getCurrentUser();
    return u?.role === 'client' || u?.role === 'affilie';
  };

  // Load balance information for the client
  const loadBalanceInfo = async (clientId, refDate = null) => {
    if (!clientId) {
      setBalanceInfo(null);
      setBalanceDisplay(null);
      return;
    }

    // Skip balance loading for admin users (NEW)
    const currentUser = getCurrentUser();
    if (isAdminSpaceUser(currentUser)) {
      setBalanceInfo(null);
      setBalanceDisplay(null);
      setBalanceLoading(false);
      return;
    }

    setBalanceLoading(true);
    try {
      const balanceDate = toBalanceRefDate(refDate);
      const balance = await BalanceValidationApi.getPartnerBalance(clientId, balanceDate);
      console.log('[BALANCE] Loaded balance for partner:', clientId, {
        hasPlafond: balance?.plafond?.hasPlafond,
        plafondAmount: balance?.plafond?.availableAmount,
        hasDeposit: balance?.deposit?.hasDeposit,
        depositAmount: balance?.deposit?.availableAmount
      });
      setBalanceInfo(balance);
      const formatted = BalanceValidationApi.formatBalanceDisplay(balance);
      setBalanceDisplay(formatted);

      // After balance is loaded, check if we need to auto-split payment methods
      if (orderedMethods.length > 0 && totalAmount > 0) {
        autoSplitPaymentMethods(balance, orderedMethods, totalAmount);
      }
    } catch (error) {
      console.error('[BALANCE] Error loading balance:', error);
      console.error(t('moteurReservation.automaticPaymentMethods.error_loading_balance_info'));
      setBalanceInfo(null);
      setBalanceDisplay(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  // Automatically split payment methods based on available balance
  const autoSplitPaymentMethods = (balanceInfo, methods, totalAmountToSplit) => {
    if (!balanceInfo || !methods || methods.length === 0 || totalAmountToSplit <= 0) {
      return;
    }

    const validTotalAmount = parseFloat(totalAmountToSplit) || 0;
    const firstMethod = methods[0];
    const secondMethod = methods.length > 1 ? methods[1] : null;

    if (!firstMethod) return;

    const firstMethodType = normalizePaymentMethodName(firstMethod.type);
    let availableForFirstMethod = 0;

    // Check available balance for first method
    if (firstMethodType === 'debiteur' && balanceInfo.plafond && balanceInfo.plafond.hasPlafond) {
      availableForFirstMethod = balanceInfo.plafond.availableAmount || 0;
      // } else if (firstMethodType === 'deposit' && balanceInfo.deposit && balanceInfo.deposit.hasDeposit) {
    } else if (firstMethodType === 'deposit' && balanceInfo.deposit) {
      availableForFirstMethod = balanceInfo.deposit.availableAmount || 0;
    } else {
      // For other payment types (totalite_en_ligne, total_marge), use full amount
      availableForFirstMethod = validTotalAmount;
    }

    // If first method can cover the full amount, use only first method
    if (availableForFirstMethod >= validTotalAmount) {
      const newMethods = {
        [firstMethod.type]: validTotalAmount,
      };
      setPaymentMethods(newMethods);
      setTotalCalculated(validTotalAmount);
      onChange?.(normalizePaymentMethods(newMethods));
    }
    // Force split if: insufficient balance AND (no current methods OR only one method configured)
    else if (availableForFirstMethod > 0 && availableForFirstMethod < validTotalAmount) {
      const remainingAmount = validTotalAmount - availableForFirstMethod;
      let fallbackMethodType = null;

      // Check if we have a configured second method
      if (secondMethod) {
        fallbackMethodType = secondMethod.type;
      } else {
        // Find a fallback method
        const currentNormalizedType = normalizePaymentMethodName(firstMethod.type);
        const fallbackMethods = ['deposit', 'totalite_en_ligne', 'debiteur', 'total_marge'].filter(
          type => type !== currentNormalizedType
        );

        if (fallbackMethods.length > 0) {
          fallbackMethodType = fallbackMethods[0];
        }
      }

      if (fallbackMethodType) {
        const newMethods = {
          [firstMethod.type]: availableForFirstMethod,
          [fallbackMethodType]: remainingAmount,
        };

        setPaymentMethods(newMethods);
        setTotalCalculated(validTotalAmount);
        onChange?.(normalizePaymentMethods(newMethods));
      } else {
        const newMethods = {
          [firstMethod.type]: availableForFirstMethod,
        };
        setPaymentMethods(newMethods);
        setTotalCalculated(availableForFirstMethod);
        onChange?.(normalizePaymentMethods(newMethods));
      }
    }
    // If first method has no available balance, use fallback method
    else if (availableForFirstMethod <= 0) {
      let fallbackMethodType = null;

      if (secondMethod) {
        fallbackMethodType = secondMethod.type;
      } else {
        const currentNormalizedType = normalizePaymentMethodName(firstMethod.type);
        const fallbackMethods = ['deposit', 'totalite_en_ligne', 'debiteur', 'total_marge'].filter(
          type => type !== currentNormalizedType
        );

        if (fallbackMethods.length > 0) {
          fallbackMethodType = fallbackMethods[0];
        }
      }

      if (fallbackMethodType) {
        const newMethods = {
          [fallbackMethodType]: validTotalAmount,
        };

        setPaymentMethods(newMethods);
        setTotalCalculated(validTotalAmount);
        onChange?.(normalizePaymentMethods(newMethods));
      } else {

        console.error(
          `${t('moteurReservation.automaticPaymentMethods.no_balance_available_for', { method: firstMethod.type })} ${t('moteurReservation.automaticPaymentMethods.and_no_alternative_payment_method_configured')}`
        );
      }
    }
  };

  // Fetch ordered payment methods for the client with simplified logic
  useEffect(() => {
    const fetchOrderedMethods = async () => {
      setLoading(true);
      setError(null);
      setFallbackMode(false);
      setB2bInsufficientDepositAndPlafond(false);
      console.log('[PAYMENT METHODS] Client:', client);
      // If no client, still show default payment methods
      if (!client?.id) {
        setB2bInsufficientDepositAndPlafond(false);
        setLoading(false);
        // Set default available methods so user can still select payment methods
        setAvailableMethods(['debiteur', 'deposit', 'totalite_en_ligne', 'total_marge']);
        setOrderedMethods([
          { order: 1, type: 'debiteur', amount: 0, isAutomatic: true }
        ]);
        setPaymentMethods({});
        setTotalCalculated(0);
        onChange?.({});
        return;
      }

      // Ensure totalAmount is a valid number
      const validTotalAmount = parseFloat(totalAmount) || 0;

      if (validTotalAmount <= 0) {
        setB2bInsufficientDepositAndPlafond(false);
        setLoading(false);
        return;
      }

      try {
        // Get partner's available payment methods to check for deposit
        const effectiveClientId = getEffectiveClientId();
        const isB2B = isB2BForEligibility();

        if (isB2B && effectiveClientId && validTotalAmount > 0) {
          const eligibilityResponse = await BalanceValidationApi.getPartnerPaymentEligibility(
            effectiveClientId,
            {
              checkInDate: bookingDate || null,
              totalAmount: validTotalAmount,
            }
          );
          const eligibility = eligibilityResponse?.data || {};

          if (eligibility.canUseDeposit) {
            setB2bInsufficientDepositAndPlafond(false);
            const forcedMethods = { deposit: validTotalAmount };
            setFallbackMode(false);
            setError(null);
            setMethodLabelsById({});
            setAvailableMethods(['deposit']);
            setOrderedMethods([{ order: 1, type: 'deposit', amount: 0, isAutomatic: true }]);
            setPaymentMethods(forcedMethods);
            setTotalCalculated(validTotalAmount);
            onChange?.(normalizePaymentMethods(forcedMethods));
            return;
          }

          if (eligibility.canUseDebiteur) {
            setB2bInsufficientDepositAndPlafond(false);
            const forcedMethods = { debiteur: validTotalAmount };
            setFallbackMode(false);
            setError(null);
            setMethodLabelsById({});
            setAvailableMethods(['debiteur']);
            setOrderedMethods([{ order: 1, type: 'debiteur', amount: 0, isAutomatic: true }]);
            setPaymentMethods(forcedMethods);
            setTotalCalculated(validTotalAmount);
            onChange?.(normalizePaymentMethods(forcedMethods));
            return;
          }

          // Neither alone covers the total — try dépôt first, then crédit (débiteur)
          const depositInfo = eligibility.deposit || {};
          const plafondInfo = eligibility.plafond || {};
          const depositAvail = Number(depositInfo.availableAmount || 0);
          const creditAvail = Number(plafondInfo.availableAmount || 0);
          const hasDeposit =
            Boolean(depositInfo.hasDeposit) && depositAvail > 0.01;
          const hasCredit =
            Boolean(plafondInfo.hasPlafond) && creditAvail > 0.01;
          const combinedAvail =
            (hasDeposit ? depositAvail : 0) + (hasCredit ? creditAvail : 0);

          if (combinedAvail >= validTotalAmount - 0.01 && (hasDeposit || hasCredit)) {
            const balanceForSplit = {
              deposit: depositInfo,
              plafond: plafondInfo,
            };
            const orderedMethodsList = [];
            if (hasDeposit) {
              orderedMethodsList.push({
                order: 1,
                type: 'deposit',
                amount: 0,
                isAutomatic: true,
              });
            }
            if (hasCredit) {
              orderedMethodsList.push({
                order: orderedMethodsList.length + 1,
                type: 'debiteur',
                amount: 0,
                isAutomatic: true,
              });
            }

            setB2bInsufficientDepositAndPlafond(false);
            setFallbackMode(false);
            setError(null);
            setMethodLabelsById({});
            setPartnerModesById({});
            setBalanceInfo(balanceForSplit);
            setAvailableMethods(orderedMethodsList.map(m => m.type));
            setOrderedMethods(orderedMethodsList);
            autoSplitPaymentMethods(
              balanceForSplit,
              orderedMethodsList,
              validTotalAmount
            );
            return;
          }

          setB2bInsufficientDepositAndPlafond(true);

          if (isPartnerUserForTicket()) {
            setFallbackMode(false);
            setError(null);
            setMethodLabelsById({});
            setPartnerModesById({});
            setOrderedMethods([]);
            setAvailableMethods([]);
            setPaymentMethods({});
            setTotalCalculated(0);
            onChange?.({});
            return;
          }

          // Manual fallback should allow ALL payment methods (physique-like), not partner-limited (admin)
          const allModes = await BalanceValidationApi.getAllPaymentModesWithChamps();
          const allModeArray = Array.isArray(allModes) ? allModes : [];
          const allIds = allModeArray.map((m) => String(m?.id ?? '')).filter(Boolean);
          const labelsById = {};
          const modesById = {};
          allModeArray.forEach((m) => {
            if (m?.id != null) {
              labelsById[String(m.id)] = m.label || m.libelle || m.code || `Mode ${m.id}`;
              modesById[String(m.id)] = m;
            }
          });

          if (allIds.length > 0) {
            let idsForManual = allIds;
            if (isAdminUser) {
              const filtered = allIds.filter(id =>
                isAdminManualAllowedPaymentLabel(labelsById[String(id)] || '')
              );
              if (filtered.length > 0) {
                idsForManual = filtered;
              }
            }
            const manualPayAmount =
              isAdminUser &&
              economicModelPaymentAmount != null &&
              economicModelPaymentAmount !== '' &&
              Number.isFinite(parseFloat(economicModelPaymentAmount))
                ? Number(parseFloat(economicModelPaymentAmount).toFixed(2))
                : validTotalAmount;
            const firstId = isAdminUser
              ? pickDefaultEspèceModeId(idsForManual, labelsById)
              : idsForManual[0];
            const manualMethods = { [firstId]: { amount: manualPayAmount, fields: {} } };
            setFallbackMode(true);
            setError(null);
            setMethodLabelsById(labelsById);
            setPartnerModesById(modesById);
            setOrderedMethods([]);
            setAvailableMethods(idsForManual);
            setPaymentMethods(manualMethods);
            setTotalCalculated(manualPayAmount);
            onChange?.(manualMethods);
            return;
          }
        }

        const result =
          await ModelEconomiquePartenaireApi.getModelEconomiquePartenaireByPartenaireId(
            effectiveClientId
          );
        // Extract available payment methods
        let availableMethodsFromAPI = [];
        if (result && result.length > 0) {
          const config = result[0];
          if (config.type) {
            if (Array.isArray(config.type)) {
              // Handle nested array structure like [['Débiteur', 'Deposit']]
              if (config.type.length > 0 && Array.isArray(config.type[0])) {
                availableMethodsFromAPI = config.type[0];
              } else {
                availableMethodsFromAPI = config.type;
              }
            } else if (typeof config.type === 'string') {
              // Handle concatenated strings like "DébiteurDeposit"
              const paymentMethodNames = [
                'Débiteur',
                'Deposit',
                'Totalité en ligne',
                'Total-Marge',
              ];
              const typeString = config.type;

              const foundMethods = [];
              paymentMethodNames.forEach(method => {
                if (typeString.includes(method)) {
                  foundMethods.push(method);
                }
              });

              availableMethodsFromAPI = foundMethods.length > 0 ? foundMethods : [typeString];
            }
          }
        }

        // Normalize available methods
        const normalizedAvailableMethods = availableMethodsFromAPI.map(method =>
          normalizePaymentMethodName(method)
        );
        setAvailableMethods(normalizedAvailableMethods);

        // Simplified ordering logic:
        // 1. Check if deposit is available (either in config OR in balance info)
        // 2. If deposit has available balance, put it first
        // 3. Then add debiteur as fallback
        const orderedMethodsList = [];
        const hasDepositInConfig = normalizedAvailableMethods.includes('deposit');
        
        // Also check actual balance info to see if partner has deposit with available amount
        const hasDepositBalance = balanceInfo?.deposit?.hasDeposit && 
                                 balanceInfo?.deposit?.availableAmount > 0;
        
        // Use deposit first if it's either configured OR has actual balance
        const shouldUseDepositFirst = hasDepositInConfig || hasDepositBalance;

        console.log('[PAYMENT ORDER] Decision:', {
          hasDepositInConfig,
          hasDepositBalance,
          shouldUseDepositFirst,
          balanceInfo: balanceInfo ? {
            hasDeposit: balanceInfo.deposit?.hasDeposit,
            availableAmount: balanceInfo.deposit?.availableAmount
          } : 'not loaded yet'
        });

        if (shouldUseDepositFirst) {
          orderedMethodsList.push({
            order: 1,
            type: 'deposit',
            amount: 0,
            isAutomatic: true,
          });
          orderedMethodsList.push({
            order: 2,
            type: 'debiteur',
            amount: 0,
            isAutomatic: true,
          });
        } else {
          orderedMethodsList.push({
            order: 1,
            type: 'debiteur',
            amount: 0,
            isAutomatic: true,
          });
        }

        setOrderedMethods(orderedMethodsList);

        // Initialize payment methods with first method temporarily
        // The auto-split logic will run when balance info is loaded
        const firstMethod = orderedMethodsList[0];

        const initialMethods = {
          [firstMethod.type]: validTotalAmount,
        };

        setPaymentMethods(initialMethods);
        setTotalCalculated(validTotalAmount);

        // If we already have balance info, apply auto-split immediately
        if (balanceInfo) {
          autoSplitPaymentMethods(balanceInfo, orderedMethodsList, validTotalAmount);
        } else {
          // Send normalized data to parent (will be updated after balance loads)
          const normalizedMethods = normalizePaymentMethods(initialMethods);
          onChange?.(normalizedMethods);
        }
      } catch (err) {
        console.error('[AUTOMATIC PAYMENT] API error:', err);
        setB2bInsufficientDepositAndPlafond(false);
        // Fallback to manual mode on error
        setFallbackMode(true);
        setError(
          t('moteurReservation.automaticPaymentMethods.error_loading_payment_methods_mode_manual')
        );

        // Set default available methods for manual mode
        setAvailableMethods(['debiteur', 'deposit', 'totalite_en_ligne', 'total_marge']);
        setPaymentMethods({});
        setTotalCalculated(0);
        onChange?.({});
      } finally {
        setLoading(false);
      }
    };

    const effectiveClientId = getEffectiveClientId();
    if (effectiveClientId) {
      fetchOrderedMethods();
      loadBalanceInfo(effectiveClientId, bookingDate);
    } else {
      setBalanceInfo(null);
      setBalanceDisplay(null);
    }
  }, [client?.id, client?.segment, totalAmount, bookingDate, partnerIdOverride, economicModelPaymentAmount]);

  useEffect(() => {
    if (!onPartnerInsufficientBalanceChange) return undefined;
    const u = getCurrentUser();
    const isPartnerOrAffilie =
      u?.role === 'client' ||
      u?.role === 'affilie' ||
      u?.userType === 'partner';
    const blocked =
      b2bInsufficientDepositAndPlafond &&
      isPartnerOrAffilie &&
      String(client?.segment || '').toUpperCase() !== 'B2C' &&
      (parseFloat(totalAmount) || 0) > 0;
    onPartnerInsufficientBalanceChange(blocked);
    return () => onPartnerInsufficientBalanceChange(false);
  }, [
    onPartnerInsufficientBalanceChange,
    b2bInsufficientDepositAndPlafond,
    client?.segment,
    totalAmount,
  ]);

  // Partner / affilié: eligibility vs balance can disagree; if auto débiteur/dépôt is invalid, flag insufficient
  useEffect(() => {
    const u = getCurrentUser();
    const isPartnerOrAffilie =
      u?.role === 'client' ||
      u?.role === 'affilie' ||
      u?.userType === 'partner';
    if (!isPartnerOrAffilie || loading || balanceLoading) return;
    if (b2bInsufficientDepositAndPlafond || fallbackMode) return;
    if (!balanceDisplay || orderedMethods.length !== 1) return;
    const amount = parseFloat(totalAmount) || 0;
    if (amount <= 0) return;
    const first = normalizePaymentMethodName(orderedMethods[0].type);
    if (first !== 'debiteur' && first !== 'deposit') return;
    const v = BalanceValidationApi.checkPaymentAmount(balanceDisplay, first, amount);
    if (v.isValid) return;
    setB2bInsufficientDepositAndPlafond(true);
    setFallbackMode(false);
    setMethodLabelsById({});
    setPartnerModesById({});
    setOrderedMethods([]);
    setAvailableMethods([]);
    setPaymentMethods({});
    setTotalCalculated(0);
    onChange?.({});
  }, [
    balanceDisplay,
    orderedMethods,
    totalAmount,
    loading,
    balanceLoading,
    b2bInsufficientDepositAndPlafond,
    fallbackMode,
  ]);

  // Initialize payment methods from value prop (évite re-sync si contenu identique)
  useEffect(() => {
    if (!value || typeof value !== 'object') return;
    const sig = JSON.stringify(value);
    const curSig = JSON.stringify(paymentMethods);
    if (sig === curSig) return;
    setPaymentMethods(value);
    calculateTotal(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paymentMethods read for dedup only
  }, [value]);

  // Re-order methods when balance info is loaded
  useEffect(() => {
    if (balanceInfo && balanceInfo.deposit?.hasDeposit && balanceInfo.deposit?.availableAmount > 0) {
      console.log('[PAYMENT ORDER] Balance loaded, re-ordering methods to prioritize deposit');
      
      // Check if deposit is already first
      if (orderedMethods.length > 0 && orderedMethods[0].type !== 'deposit') {
        // Re-order to put deposit first
        const newOrderedMethods = [
          {
            order: 1,
            type: 'deposit',
            amount: 0,
            isAutomatic: true,
          },
          {
            order: 2,
            type: 'debiteur',
            amount: 0,
            isAutomatic: true,
          },
        ];
        
        setOrderedMethods(newOrderedMethods);
        
        // Re-run auto-split with new order
        if (totalAmount > 0) {
          autoSplitPaymentMethods(balanceInfo, newOrderedMethods, totalAmount);
        }
      }
    }
  }, [balanceInfo]);

  // Re-run auto-split when total amount changes (after balance info is loaded)
  useEffect(() => {
    if (balanceInfo && orderedMethods.length > 0 && totalAmount > 0 && !fallbackMode) {
      // Small delay to avoid conflicts with other updates
      const timer = setTimeout(() => {
        autoSplitPaymentMethods(balanceInfo, orderedMethods, totalAmount);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [totalAmount, balanceInfo, orderedMethods, fallbackMode]);

  // Calculate total from payment methods
  const calculateTotal = methods => {
    const total = Object.values(methods).reduce((sum, val) => {
      const validAmount = extractAmount(val);
      return sum + validAmount;
    }, 0);
    setTotalCalculated(total);
    return total;
  };

  // Keep admin manual fallback amounts aligned with eco-model "à payer maintenant" when it changes
  useEffect(() => {
    if (!isAdminUser || !fallbackMode) return;
    if (economicModelPaymentAmount == null || economicModelPaymentAmount === '') return;
    const amt = Number(parseFloat(economicModelPaymentAmount).toFixed(2));
    if (!Number.isFinite(amt)) return;
    setPaymentMethods((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const currentSum = Object.values(prev).reduce((s, v) => s + extractAmount(v), 0);
      if (Math.abs(currentSum - amt) < 0.01) return prev;
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const fields = typeof v === 'object' && v?.fields ? v.fields : {};
        next[k] = { amount: amt, fields };
      });
      queueMicrotask(() => onChange?.(next));
      return next;
    });
    setTotalCalculated(amt);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange is stable enough; avoid parent re-render loops
  }, [economicModelPaymentAmount, isAdminUser, fallbackMode]);

  // Update payment method amount (automatic mode)
  const updatePaymentMethod = (methodType, amount) => {
    const validTotalAmount = parseFloat(totalAmount) || 0;
    let validAmount = parseFloat(amount) || 0;
    if (validAmount > validTotalAmount) validAmount = validTotalAmount;
    if (validAmount < 0) validAmount = 0;

    const updatedMethods = { ...paymentMethods };

    // Check if this is the first method and we need to respect balance limits
    // Skip balance checks for admin users (NEW)
    const currentUser = getCurrentUser();
    const isAdmin = isAdminSpaceUser(currentUser);

    if (methodType === orderedMethods[0]?.type && balanceInfo && !isAdmin) {
      const firstMethodType = normalizePaymentMethodName(methodType);
      let availableBalance = validAmount; // Default to the amount user entered

      // Check available balance for first method
      if (firstMethodType === 'debiteur' && balanceInfo.plafond && balanceInfo.plafond.hasPlafond) {
        availableBalance = Math.min(validAmount, balanceInfo.plafond.availableAmount || 0);
      } else if (
        firstMethodType === 'deposit' &&
        balanceInfo.deposit &&
        balanceInfo.deposit.hasDeposit
      ) {
        availableBalance = Math.min(validAmount, balanceInfo.deposit.availableAmount || 0);
      }

      // If user entered more than available balance, we need to split
      if (validAmount > availableBalance && availableBalance >= 0) {
        updatedMethods[methodType] = availableBalance;
        const remainingAmount = validAmount - availableBalance; // Amount that exceeds balance

        if (remainingAmount > 0.01) {
          // Find or create a second method
          let secondMethodType = null;

          // Check if we have a configured second method
          if (orderedMethods.length > 1) {
            secondMethodType = orderedMethods[1].type;
          } else {
            // Create a fallback method - find an alternative payment type
            const currentNormalizedType = normalizePaymentMethodName(methodType);
            const fallbackMethods = [
              'deposit',
              'totalite_en_ligne',
              'debiteur',
              'total_marge',
            ].filter(type => type !== currentNormalizedType);

            // Check if we have any available methods that aren't the current one
            const availableFallback = availableMethods.find(
              method => normalizePaymentMethodName(method) !== currentNormalizedType
            );

            if (availableFallback) {
              secondMethodType = availableFallback;
            } else if (fallbackMethods.length > 0) {
              secondMethodType = fallbackMethods[0];
            }
          }

          if (secondMethodType) {
            updatedMethods[secondMethodType] = remainingAmount;
          } else {

            console.warn(
              `${t('moteurReservation.automaticPaymentMethods.insufficient_balance_for', { method: methodType })} ${t('moteurReservation.automaticPaymentMethods.no_alternative_payment_method_available')}`
            );
          }
        }
      } else {
        // User entered amount within balance limits
        updatedMethods[methodType] = validAmount;
        const remainingAmount = validTotalAmount - validAmount;

        if (remainingAmount > 0.01 && orderedMethods.length > 1) {
          updatedMethods[orderedMethods[1].type] = remainingAmount;
        } else if (orderedMethods.length > 1) {
          // Remove second method if first method covers full amount
          delete updatedMethods[orderedMethods[1].type];
        }
      }
    } else {
      // For other methods or when no balance info, use original logic
      updatedMethods[methodType] = validAmount;

      // If this is the first method and there's a second method available
      if (methodType === orderedMethods[0]?.type && orderedMethods.length > 1) {
        const remainingAmount = validTotalAmount - validAmount;

        if (remainingAmount > 0.01) {
          updatedMethods[orderedMethods[1].type] = remainingAmount;
        } else {
          delete updatedMethods[orderedMethods[1].type];
        }
      }
    }

    setPaymentMethods(updatedMethods);
    calculateTotal(updatedMethods);
    // Send normalized data to parent
    onChange?.(normalizePaymentMethods(updatedMethods));
  };

  // Add manual payment method
  const addManualPaymentMethod = () => {
    const selectedTypes = Object.keys(paymentMethods);
    const availableTypes = availableMethods.filter(type => !selectedTypes.includes(type));

    if (availableTypes.length === 0) {
      console.warn(t('moteurReservation.automaticPaymentMethods.all_payment_methods_added'));
      return;
    }

    const newMethodType = availableTypes[0];

    const updatedMethods = { ...paymentMethods };
    updatedMethods[newMethodType] = { amount: 0, fields: {} };

    setPaymentMethods(updatedMethods);
    calculateTotal(updatedMethods);
    onChange?.(updatedMethods);
  };

  // Remove manual payment method
  const removeManualPaymentMethod = methodType => {
    const updatedMethods = { ...paymentMethods };
    delete updatedMethods[methodType];

    setPaymentMethods(updatedMethods);
    calculateTotal(updatedMethods);
    onChange?.(updatedMethods);
  };

  const roundMoney = (n) => Math.max(0, Math.round((Number(n) || 0) * 100) / 100);

  const sumOtherManualAmounts = (methods, excludeType) =>
    Object.entries(methods || {}).reduce((sum, [key, val]) => {
      if (key === excludeType) return sum;
      return sum + (extractAmount(val) || 0);
    }, 0);

  const getMaxAmountForMethod = (methodType, methods = paymentMethods) => {
    const total = roundMoney(totalAmount);
    const others = roundMoney(sumOtherManualAmounts(methods, methodType));
    return roundMoney(Math.max(0, Math.min(total, total - others)));
  };

  const clampManualAmount = (methodType, amount, methods = paymentMethods) => {
    if (amount === '' || amount == null) return 0;
    const raw = parseFloat(amount);
    if (!Number.isFinite(raw)) return 0;
    const maxForThis = getMaxAmountForMethod(methodType, methods);
    return roundMoney(Math.min(Math.max(0, raw), maxForThis));
  };

  const updateManualPaymentMethodAmount = (methodType, amount) => {
    const validAmount = clampManualAmount(methodType, amount);
    const updatedMethods = { ...paymentMethods };
    const current = updatedMethods[methodType];
    const currentFields =
      typeof current === 'object' && current?.fields ? current.fields : {};
    updatedMethods[methodType] = { amount: validAmount, fields: currentFields };
    setPaymentMethods(updatedMethods);
    calculateTotal(updatedMethods);
    onChange?.(updatedMethods);
  };

  const updateManualPaymentMethodFields = (methodType, fields) => {
    const updatedMethods = { ...paymentMethods };
    const current = updatedMethods[methodType];
    const currentAmount = extractAmount(current);
    updatedMethods[methodType] = { amount: currentAmount, fields: fields || {} };
    setPaymentMethods(updatedMethods);
    calculateTotal(updatedMethods);
    onChange?.(updatedMethods);
  };

  const changeManualPaymentMethodType = (oldType, newType) => {
    if (!newType || oldType === newType) return;
    if (paymentMethods[newType] != null) return;
    const updatedMethods = { ...paymentMethods };
    const oldVal = updatedMethods[oldType];
    delete updatedMethods[oldType];
    const oldAmount = extractAmount(oldVal);
    const oldFields =
      typeof oldVal === 'object' && oldVal?.fields ? oldVal.fields : {};
    updatedMethods[newType] = { amount: oldAmount, fields: oldFields };
    setPaymentMethods(updatedMethods);
    calculateTotal(updatedMethods);
    onChange?.(updatedMethods);
  };

  /**
   * Tamarra icon tiles: add a payment type (multi allowed).
   * Prefills with total − sum already typed on other methods.
   */
  const selectManualPaymentMethod = (methodType) => {
    if (!methodType) return;
    if (paymentMethods[methodType] != null) return;

    const total = roundMoney(totalAmount);
    const entered = roundMoney(sumOtherManualAmounts(paymentMethods, null));
    const remainder = roundMoney(Math.max(0, total - entered));

    const updatedMethods = {
      ...paymentMethods,
      [methodType]: { amount: remainder, fields: {} },
    };
    setPaymentMethods(updatedMethods);
    calculateTotal(updatedMethods);
    onChange?.(updatedMethods);
  };

  // Validate total
  const validateTotal = () => {
    const difference = Math.abs(totalCalculated - totalAmount);
    return difference <= 0.01; // Allow small rounding differences
  };

  // Check if payment methods are valid
  const validatePaymentMethods = () => {
    const hasMethods = Object.keys(paymentMethods).length > 0;
    const hasValidAmounts = Object.values(paymentMethods).some(amount => parseFloat(amount) > 0);
    return hasMethods && hasValidAmounts;
  };

  // Get payment method labels
  const getPaymentTypeLabels = () => {
    return {
      // Normalized names (from backend)
      debiteur: t('moteurReservation.automaticPaymentMethods.debit'),
      deposit: t('moteurReservation.automaticPaymentMethods.deposit'),
      totalite_en_ligne: t('moteurReservation.automaticPaymentMethods.total_online'),
      total_marge: t('moteurReservation.automaticPaymentMethods.total_margin'),
      // Original names (for backward compatibility)
      Débiteur: t('moteurReservation.automaticPaymentMethods.debit'),
      Deposit: t('moteurReservation.automaticPaymentMethods.deposit'),
      'Totalité en ligne': t('moteurReservation.automaticPaymentMethods.total_online'),
      'Total-Marge': t('moteurReservation.automaticPaymentMethods.total_margin'),
      ...methodLabelsById,
    };
  };

  const labels = getPaymentTypeLabels();

  const manualMethodIdsForDropdown =
    isAdminUser && fallbackMode
      ? (() => {
          const filtered = availableMethods.filter(id =>
            isAdminManualAllowedPaymentLabel(labels[id] || methodLabelsById[id] || '')
          );
          return filtered.length > 0 ? filtered : availableMethods;
        })()
      : availableMethods;

  // Render balance information
  const renderBalanceInfo = () => {
    if (!balanceDisplay) return null;

    return (
      <div className='mb-4'>
        <div className='mb-2'>
          <span className='font-medium'>
            <svg
              className='inline h-4 w-4 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            {t('moteurReservation.automaticPaymentMethods.balance_info_title')}
            <span className='text-xs text-gray-500 ml-2'>
              {t('moteurReservation.automaticPaymentMethods.hover_to_see_details')}
            </span>
          </span>

          {/* Show basic balance summary */}
          <div className='mt-1 text-xs text-gray-600'>
            {balanceDisplay.plafond && (
              <span className='mr-4'>
                {t('moteurReservation.automaticPaymentMethods.plafond')}:{' '}
                <span
                  className={balanceDisplay.plafond.isValid ? 'text-green-600' : 'text-red-600'}
                >
                  {partnerDisplay && balanceInfo?.plafond != null
                    ? `${displayAmount(balanceInfo.plafond.availableAmount || 0).toFixed(2)} ${displayCurrency}`
                    : balanceDisplay.plafond.formatted.available}
                </span>
              </span>
            )}
            {balanceDisplay.deposit && (
              <span>
                {t('moteurReservation.automaticPaymentMethods.deposit')}:{' '}
                <span
                  className={balanceDisplay.deposit.isValid ? 'text-green-600' : 'text-red-600'}
                >
                  {partnerDisplay && balanceInfo?.deposit != null
                    ? `${displayAmount(balanceInfo.deposit.availableAmount || 0).toFixed(2)} ${displayCurrency}`
                    : balanceDisplay.deposit.formatted.available}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Info alerts are always visible as they explain the payment ordering logic */}
        {orderedMethods.length === 1 && (
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2'>
            <div className='flex items-start'>
              <div className='flex-shrink-0'>
                <svg className='h-5 w-5 text-blue-400' viewBox='0 0 20 20' fill='currentColor'>
                  <path
                    fillRule='evenodd'
                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-blue-800'>
                  {t('moteurReservation.automaticPaymentMethods.info_title')}
                </h3>
                <div className='mt-1 text-sm text-blue-700'>
                  {t('moteurReservation.automaticPaymentMethods.info_description')}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Get validation message for payment method amount
  const getPaymentValidation = (methodType, amount) => {
    if (!balanceDisplay || !amount) return null;

    const paymentType = normalizePaymentMethodName(methodType);
    if (paymentType !== 'debiteur' && paymentType !== 'deposit') return null;

    const validation = BalanceValidationApi.checkPaymentAmount(balanceDisplay, paymentType, amount);
    if (!validation.message) return null;

    let message = validation.message;
    if (partnerDisplay && validation.severity === 'success' && balanceInfo) {
      if (paymentType === 'deposit' && balanceInfo.deposit) {
        const rest = (balanceInfo.deposit.availableAmount || 0) - (parseFloat(amount) || 0);
        message = `Dépôt OK (reste: ${displayAmount(rest).toFixed(2)} ${displayCurrency})`;
      } else if (paymentType === 'debiteur' && balanceInfo.plafond) {
        const rest = (balanceInfo.plafond.availableAmount || 0) - (parseFloat(amount) || 0);
        message = `Plafond OK (reste: ${displayAmount(rest).toFixed(2)} ${displayCurrency})`;
      }
    }

    const iconMap = {
      success: (
        <svg className='inline h-4 w-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
          <path
            fillRule='evenodd'
            d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
            clipRule='evenodd'
          />
        </svg>
      ),
      warning: (
        <svg className='inline h-4 w-4 text-yellow-500' fill='currentColor' viewBox='0 0 20 20'>
          <path
            fillRule='evenodd'
            d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
            clipRule='evenodd'
          />
        </svg>
      ),
      error: (
        <svg className='inline h-4 w-4 text-red-500' fill='currentColor' viewBox='0 0 20 20'>
          <path
            fillRule='evenodd'
            d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
            clipRule='evenodd'
          />
        </svg>
      ),
    };

    return (
      <div className='mt-1 text-xs'>
        {iconMap[validation.severity]} {message}
      </div>
    );
  };

  const tamarraUi = appearance === 'tamarra'
  const shellClass = tamarraUi
    ? 'tamarra-debtor'
    : 'bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4'

  if (!tamarraUi && (loading || balanceLoading)) {
    return (
      <div className={shellClass}>
        <h3 className='text-lg font-medium text-gray-900 mb-4'>
          {t('moteurReservation.automaticPaymentMethods.payment_methods_title')}
        </h3>
        <div style={{ textAlign: 'center', padding: 16 }}>
          <span className='text-gray-500'>
            {t('moteurReservation.automaticPaymentMethods.loading_payment_methods')}
          </span>
        </div>
      </div>
    );
  }

  const hideManualPaymentForPartnerCredit =
    b2bInsufficientDepositAndPlafond &&
    isPartnerUserForTicket() &&
    String(client?.segment || '').toUpperCase() !== 'B2C' &&
    (parseFloat(totalAmount) || 0) > 0;

  if (tamarraUi) {
    return (
      <TamarraDebtorDepositUi
        t={t}
        loading={loading || balanceLoading}
        disabled={disabled}
        error={error}
        fallbackMode={fallbackMode}
        hideManual={hideManualPaymentForPartnerCredit}
        lockPaymentAmount={lockPaymentAmount}
        lockPaymentAmountMessage={lockPaymentAmountMessage}
        partnerDisplay={partnerDisplay}
        displayAmount={displayAmount}
        displayToTnd={displayToTnd}
        displayCurrency={displayCurrency}
        totalAmount={totalAmount}
        totalCalculated={totalCalculated}
        totalOk={validateTotal()}
        labels={labels}
        paymentMethods={paymentMethods}
        availableMethods={availableMethods}
        orderedMethods={orderedMethods}
        partnerModesById={partnerModesById}
        manualMethodIdsForDropdown={manualMethodIdsForDropdown}
        balanceDisplay={balanceDisplay}
        balanceInfo={balanceInfo}
        creditNotice={
          <PartnerCreditInsufficiencyNotice
            visible={
              !isAdminUser &&
              b2bInsufficientDepositAndPlafond &&
              String(client?.segment || '').toUpperCase() !== 'B2C' &&
              (parseFloat(totalAmount) || 0) > 0
            }
            showTicketButton={isPartnerUserForTicket()}
            onOpenTicket={handleOpenSupplierReclamationTicket}
            ticketCreating={ticketCreating}
          />
        }
        showFallbackNotice={fallbackMode && !hideManualPaymentForPartnerCredit && !isAdminUser}
        showIncompleteNotice={
          !validatePaymentMethods() &&
          !loading &&
          !hideManualPaymentForPartnerCredit &&
          !isAdminUser
        }
        updatePaymentMethod={updatePaymentMethod}
        updateManualPaymentMethodAmount={updateManualPaymentMethodAmount}
        updateManualPaymentMethodFields={updateManualPaymentMethodFields}
        changeManualPaymentMethodType={changeManualPaymentMethodType}
        selectManualPaymentMethod={selectManualPaymentMethod}
        addManualPaymentMethod={addManualPaymentMethod}
        removeManualPaymentMethod={removeManualPaymentMethod}
        extractAmount={extractAmount}
        getMaxAmountForMethod={getMaxAmountForMethod}
        getPaymentValidation={getPaymentValidation}
      />
    )
  }

  return (
    <div className={shellClass}>
      <h3 className='text-lg font-medium text-gray-900 mb-4'>
        {hideManualPaymentForPartnerCredit
          ? t('moteurReservation.automaticPaymentMethods.payment_methods_title')
          : fallbackMode
            ? t('moteurReservation.automaticPaymentMethods.manual_payment_methods_title')
            : t('moteurReservation.automaticPaymentMethods.automatic_payment_methods_title')}
      </h3>
      <PartnerCreditInsufficiencyNotice
        visible={
          !isAdminUser &&
          b2bInsufficientDepositAndPlafond &&
          String(client?.segment || '').toUpperCase() !== 'B2C' &&
          (parseFloat(totalAmount) || 0) > 0
        }
        showTicketButton={isPartnerUserForTicket()}
        onOpenTicket={handleOpenSupplierReclamationTicket}
        ticketCreating={ticketCreating}
      />
      {fallbackMode && !hideManualPaymentForPartnerCredit && !isAdminUser && (
        <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4'>
          <div className='flex items-start'>
            <div className='flex-shrink-0'>
              <svg className='h-5 w-5 text-yellow-400' viewBox='0 0 20 20' fill='currentColor'>
                <path
                  fillRule='evenodd'
                  d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-yellow-800'>
                {t(
                  'moteurReservation.automaticPaymentMethods.payment_method_configuration_not_found'
                )}
              </h3>
              <div className='mt-2 text-sm text-yellow-700'>
                {t(
                  'moteurReservation.automaticPaymentMethods.no_payment_method_configuration_found'
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-4'>
          <div className='flex items-start'>
            <div className='flex-shrink-0'>
              <svg className='h-5 w-5 text-red-400' viewBox='0 0 20 20' fill='currentColor'>
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-800'>
                {t('moteurReservation.automaticPaymentMethods.error_title')}
              </h3>
              <div className='mt-2 text-sm text-red-700'>{error}</div>
            </div>
          </div>
        </div>
      )}

      {!validatePaymentMethods() && !loading && !hideManualPaymentForPartnerCredit && !isAdminUser && (
        <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4'>
          <div className='flex items-start'>
            <div className='flex-shrink-0'>
              <svg className='h-5 w-5 text-yellow-400' viewBox='0 0 20 20' fill='currentColor'>
                <path
                  fillRule='evenodd'
                  d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-yellow-800'>
                {t('moteurReservation.automaticPaymentMethods.incomplete_payment_configuration')}
              </h3>
              <div className='mt-2 text-sm text-yellow-700'>
                {t('moteurReservation.automaticPaymentMethods.no_valid_payment_method_configured')}
              </div>
            </div>
          </div>
        </div>
      )}

      {renderBalanceInfo()}

      {lockPaymentAmount && lockPaymentAmountMessage ? (
        <div
          className='mb-4 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2.5 text-sm text-sky-950'
          role='status'
        >
          {lockPaymentAmountMessage}
        </div>
      ) : null}

      <div className='mb-4'>
        <span className='font-medium'>
          {t('moteurReservation.automaticPaymentMethods.total_amount')}: {displayAmount(totalAmount).toFixed(2)} {displayCurrency}
        </span>
        <br />
        <span className={validateTotal() ? 'text-green-600' : 'text-red-600'}>
          {t('moteurReservation.automaticPaymentMethods.total_entered')}:{' '}
          {displayAmount(totalCalculated).toFixed(2)} {displayCurrency}
          {!validateTotal() &&
            ` (${t('moteurReservation.automaticPaymentMethods.difference')}: ${displayAmount(totalAmount - totalCalculated).toFixed(2)} ${displayCurrency})`}
        </span>
        <br />
      </div>

      {!hideManualPaymentForPartnerCredit && !fallbackMode ? (
        // Automatic mode - show all available payment methods (even with 0 amount)
        // If no methods in paymentMethods, show availableMethods
        (Object.keys(paymentMethods).length > 0
          ? Object.keys(paymentMethods)
          : availableMethods.length > 0
            ? availableMethods
            : ['debiteur', 'deposit', 'totalite_en_ligne', 'total_marge'])
          .map((methodType, index) => {
            // Find the method configuration or create a default one
            const method = orderedMethods.find(m => m.type === methodType) || {
              type: methodType,
              order: index + 1,
            };

            const methodAmount = paymentMethods[methodType] || 0;

            return (
              <div
                key={methodType}
                className='bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-2'
              >
                <div className='flex flex-col lg:flex-row lg:items-center lg:space-x-4 space-y-3 lg:space-y-0'>
                  <div className='min-w-[200px]'>
                    <span className='font-medium'>
                      {t('moteurReservation.automaticPaymentMethods.method_order', {
                        order: method.order,
                      })}
                      : {labels[methodType] || methodType}
                    </span>
                    <br />
                    <span className='text-gray-500 text-sm'>
                      {t('moteurReservation.automaticPaymentMethods.automatic')}
                    </span>
                    {index > 0 && methodAmount > 0 && (
                      <>
                        <br />
                        <span className='text-xs text-blue-500'>
                          {t('moteurReservation.automaticPaymentMethods.automatic_split')}
                        </span>
                      </>
                    )}
                  </div>

                  <div className='flex-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      {partnerDisplay ? `Montant (${displayCurrency})` : t('moteurReservation.automaticPaymentMethods.amount_label')}
                    </label>
                    <input
                      type='number'
                      value={partnerDisplay ? (methodAmount === '' || methodAmount == null ? '' : displayAmount(methodAmount)) : methodAmount}
                      onChange={e => {
                        if (lockPaymentAmount) return
                        const raw = e.target.value;
                        const valueTnd = raw === '' ? '' : displayToTnd(raw);
                        updatePaymentMethod(methodType, valueTnd);
                      }}
                      min='0'
                      max={partnerDisplay ? displayAmount(totalAmount) : totalAmount}
                      step={partnerDisplay ? '0.01' : '0.01'}
                      disabled={disabled || lockPaymentAmount || (index > 0 && methodAmount === 0)}
                      readOnly={lockPaymentAmount}
                      aria-readonly={lockPaymentAmount ? 'true' : undefined}
                      className='w-full px-4 py-2 border border-gray-300 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
                      placeholder={t(
                        'moteurReservation.automaticPaymentMethods.amount_placeholder',
                        { defaultValue: 'Entrez le montant...' }
                      )}
                    />
                    {getPaymentValidation(methodType, methodAmount)}
                  </div>
                </div>
              </div>
            );
          })
      ) : !hideManualPaymentForPartnerCredit ? (
        // Manual mode (partner methods) - physique-like UI (select + amount + dynamic fields)
        <>
          {Object.entries(paymentMethods).map(([methodType, paymentValue], index) => (
            <div
              key={index}
              className='bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-2'
            >
              <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0'>
                <div className='flex flex-col lg:flex-row lg:items-center lg:space-x-4 flex-1 space-y-3 lg:space-y-0'>
                  <div className='min-w-[200px]'>
                    <span className='font-medium'>
                      {t('moteurReservation.reservationModal.paymentMethod', { defaultValue: 'Payment Method' })}
                    </span>
                    <br />
                    <span className='text-gray-500 text-sm'>
                      {t('moteurReservation.automaticPaymentMethods.manual')}
                    </span>
                  </div>

                  <div className='flex-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      {t('moteurReservation.reservationModal.paymentMethod', { defaultValue: 'Payment Method' })}
                    </label>
                    <select
                      className='w-full px-4 py-2 border border-gray-300 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
                      value={methodType}
                      disabled={disabled}
                      onChange={(e) => changeManualPaymentMethodType(methodType, e.target.value)}
                    >
                      {manualMethodIdsForDropdown.map((id) => (
                        <option key={id} value={id}>
                          {labels[id] || id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='flex-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      {partnerDisplay ? `Montant (${displayCurrency})` : t('moteurReservation.automaticPaymentMethods.amount_label')}
                    </label>
                    <input
                      type='number'
                      value={partnerDisplay ? displayAmount(extractAmount(paymentValue)) : extractAmount(paymentValue)}
                      onChange={e => {
                        if (lockPaymentAmount) return
                        const raw = e.target.value;
                        const valueTnd = raw === '' ? '' : displayToTnd(raw);
                        updateManualPaymentMethodAmount(methodType, valueTnd);
                      }}
                      min='0'
                      max={partnerDisplay ? displayAmount(totalAmount) : totalAmount}
                      step='0.01'
                      disabled={disabled || lockPaymentAmount}
                      readOnly={lockPaymentAmount}
                      aria-readonly={lockPaymentAmount ? 'true' : undefined}
                      className='w-full px-4 py-2 border border-gray-300 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
                      placeholder={t(
                        'moteurReservation.automaticPaymentMethods.amount_placeholder'
                      )}
                    />
                    {getPaymentValidation(methodType, extractAmount(paymentValue))}
                  </div>
                </div>

                <button
                  type='button'
                  onClick={() => removeManualPaymentMethod(methodType)}
                  disabled={disabled}
                  className='lg:ml-4 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-start lg:self-center'
                >
                  <TrashIcon className='h-4 w-4' />
                </button>
              </div>

              {partnerModesById?.[methodType] && (
                <div className='mt-3'>
                  <PaymentMethodFields
                    paymentMethod={partnerModesById[methodType]}
                    fieldValues={(typeof paymentValue === 'object' && paymentValue?.fields) ? paymentValue.fields : {}}
                    onFieldChange={(fields) => updateManualPaymentMethodFields(methodType, fields)}
                    disabled={disabled}
                    amount={extractAmount(paymentValue)}
                    requireAmount={false}
                    variant={tamarraUi ? 'tamarra' : 'default'}
                    displayCurrency={displayCurrency}
                    displayAmount={displayAmount}
                  />
                </div>
              )}
            </div>
          ))}

          {availableMethods.length > Object.keys(paymentMethods).length && (
            <button
              type='button'
              onClick={addManualPaymentMethod}
              disabled={disabled}
              className='w-full mt-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-2xl text-gray-500 hover:border-primary-500 hover:text-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center'
            >
              <PlusIcon className='h-4 w-4 mr-2' />
              {t('moteurReservation.automaticPaymentMethods.add_payment_method')}
            </button>
          )}
        </>
      ) : null}

      {orderedMethods.length === 0 && !fallbackMode && !loading && !hideManualPaymentForPartnerCredit && (
        <div className='text-center py-4'>
          <span className='text-gray-500'>
            {t('moteurReservation.automaticPaymentMethods.no_payment_method_configured')}
          </span>
        </div>
      )}

      {Object.keys(paymentMethods).length === 0 && fallbackMode && !loading && !hideManualPaymentForPartnerCredit && (
        <div className='text-center py-4'>
          <span className='text-gray-500'>
            {t('moteurReservation.automaticPaymentMethods.no_payment_method_added')}
          </span>
        </div>
      )}
    </div>
  );
};

export default AutomaticPaymentMethods;
